import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Platform } from "react-native";

import { APP_BUILD_NUMBER } from "$src/core/constants/env";
import { DAY } from "$src/core/constants/time";
import { storage } from "$src/core/services/storage/storage";

const PREFIX = `query-cache.${APP_BUILD_NUMBER}.`;

const DEFAULT_PERSIST_GC_TIME = 7 * DAY;

type Cached<TData> = { data: TData; dataUpdatedAt: number };

const read = <TData>(key: string, maxAge: number): Cached<TData> | null => {
  const raw = storage.getString(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Cached<TData>;
    if (Date.now() - parsed.dataUpdatedAt > maxAge) {
      storage.remove(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const usePersistedQuery = <TData>({
  persistGcTime = DEFAULT_PERSIST_GC_TIME,
  ...options
}: UseQueryOptions<TData> & {
  queryKey: readonly unknown[];
  persistGcTime?: number;
}) => {
  const cacheKey = PREFIX + JSON.stringify(options.queryKey);

  const [cached] = useState(() => read<TData>(cacheKey, persistGcTime));

  const result = useQuery({
    ...options,
    initialData: cached?.data,
    initialDataUpdatedAt: cached?.dataUpdatedAt,
  });

  const { data, dataUpdatedAt } = result;

  /* Persist the query result to MMKV */
  useEffect(() => {
    // Skipped on web for now, requestIdleCallback is missing on older Safari
    // TODO: Should we improve this?
    if (Platform.OS === "web") return undefined;

    if (data === undefined || dataUpdatedAt === cached?.dataUpdatedAt) {
      return undefined;
    }

    // Defer the serialize + MMKV write to an idle frame so it never competes with the first frames during busy startup.
    const id = requestIdleCallback(
      () => storage.set(cacheKey, JSON.stringify({ data, dataUpdatedAt })),
      { timeout: 2000 },
    );

    return () => cancelIdleCallback(id);
  }, [cacheKey, cached, data, dataUpdatedAt]);

  return result;
};

export default usePersistedQuery;
